import React from 'react';
import { devFeatures, htmlCode, tsCode } from '../data/mockData';
import { Zap, Shield, Clock, Code, Terminal, Copy } from 'lucide-react';

const iconMap = {
  Zap: Zap,
  Shield: Shield,
  Clock: Clock,
  Code: Code,
};

const DeveloperSection = () => {
  const [copiedHtml, setCopiedHtml] = React.useState(false);
  const [copiedTs, setCopiedTs] = React.useState(false);

  const handleCopy = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => {
      setter(true);
      setTimeout(() => setter(false), 2000);
    });
  };

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <span className="text-xs font-bold tracking-[0.2em] text-blue-600 uppercase">
            Fejlesztőkre szabva
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Integrálja percek alatt
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Express 5 + TypeScript stack. Egyetlen JS kódrészlet, és a widget máris él a webshopodban.
          </p>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {devFeatures.map((feature) => {
            const Icon = iconMap[feature.icon];
            return (
              <div
                key={feature.title}
                className="flex flex-col items-center text-center p-4 sm:p-6 bg-white border border-gray-100 rounded-2xl hover:shadow-md hover:shadow-blue-50 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{feature.title}</h3>
                <p className="text-xs text-gray-500">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Code blocks */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* HTML Code */}
          <div className="bg-[#1e293b] rounded-2xl overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300 font-mono">widget-embed.html</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-blue-500/20 text-blue-300">
                  HTML
                </span>
                <button
                  onClick={() => handleCopy(htmlCode, setCopiedHtml)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5 overflow-x-auto">
              <pre className="text-sm font-mono leading-relaxed">
                <code>
                  <span className="text-gray-500">{'<!-- 1 perces integráció -->'}</span>{'\n'}
                  <span className="text-blue-300">{'<script'}</span>{' '}<span className="text-green-300">src</span><span className="text-gray-400">=</span><span className="text-amber-300">"https://cdn.mitvegyek.ai/widget.js"</span><span className="text-blue-300">{'></script>'}</span>{'\n'}
                  <span className="text-blue-300">{'<script>'}</span>{'\n'}
                  {'  '}<span className="text-blue-200">MitVegyek</span><span className="text-gray-400">.</span><span className="text-yellow-200">init</span><span className="text-gray-400">{'({'}</span>{'\n'}
                  {'    '}<span className="text-blue-200">apiKey</span><span className="text-gray-400">: </span><span className="text-amber-300">'mv_live_xxxxxxxxxx'</span><span className="text-gray-400">,</span>{'\n'}
                  {'    '}<span className="text-blue-200">partnerId</span><span className="text-gray-400">: </span><span className="text-amber-300">'your-shop-id'</span><span className="text-gray-400">,</span>{'\n'}
                  {'    '}<span className="text-blue-200">theme</span><span className="text-gray-400">{': {'}</span>{'\n'}
                  {'      '}<span className="text-blue-200">primary</span><span className="text-gray-400">: </span><span className="text-amber-300">'#0052CC'</span><span className="text-gray-400">,</span>{'\n'}
                  {'      '}<span className="text-blue-200">position</span><span className="text-gray-400">: </span><span className="text-amber-300">'bottom-right'</span>{'\n'}
                  {'    '}<span className="text-gray-400">{'}'}</span>{'\n'}
                  {'  '}<span className="text-gray-400">{'});'}</span>{'\n'}
                  <span className="text-blue-300">{'</script>'}</span>
                </code>
              </pre>
            </div>
          </div>

          {/* TypeScript Code */}
          <div className="hidden lg:block bg-[#1e293b] rounded-2xl overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Terminal className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300 font-mono">recommend.ts</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-blue-500/20 text-blue-300">
                  TypeScript
                </span>
                <button
                  onClick={() => handleCopy(tsCode, setCopiedTs)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="p-5 overflow-x-auto">
              <pre className="text-sm font-mono leading-relaxed">
                <code>
                  <span className="text-gray-500">// Express 5 + TypeScript</span>{'\n'}
                  <span className="text-purple-300">import</span>{' '}<span className="text-gray-400">{'{ '}</span><span className="text-blue-200">MitVegyekAPI</span><span className="text-gray-400">{' }'}</span>{' '}<span className="text-purple-300">from</span>{' '}<span className="text-amber-300">'@mitvegyek/sdk'</span><span className="text-gray-400">;</span>{'\n'}{'\n'}
                  <span className="text-purple-300">const</span>{' '}<span className="text-blue-200">api</span>{' '}<span className="text-gray-400">=</span>{' '}<span className="text-purple-300">new</span>{' '}<span className="text-yellow-200">MitVegyekAPI</span><span className="text-gray-400">{'({'}</span>{'\n'}
                  {'  '}<span className="text-blue-200">apiKey</span><span className="text-gray-400">: </span><span className="text-blue-200">process</span><span className="text-gray-400">.</span><span className="text-blue-200">env</span><span className="text-gray-400">.</span><span className="text-blue-200">MITVEGYEK_API_KEY</span>{'\n'}
                  <span className="text-gray-400">{'});'}</span>{'\n'}{'\n'}
                  <span className="text-gray-500">{'// Ajánlások lekérése'}</span>{'\n'}
                  <span className="text-purple-300">const</span>{' '}<span className="text-blue-200">results</span>{' '}<span className="text-gray-400">=</span>{' '}<span className="text-purple-300">await</span>{' '}<span className="text-blue-200">api</span><span className="text-gray-400">.</span><span className="text-yellow-200">recommend</span><span className="text-gray-400">{'({'}</span>{'\n'}
                  {'  '}<span className="text-blue-200">query</span><span className="text-gray-400">: </span><span className="text-amber-300">"sportcipő, kényelmes, 40k alatt"</span><span className="text-gray-400">,</span>{'\n'}
                  {'  '}<span className="text-blue-200">filters</span><span className="text-gray-400">{': { '}</span><span className="text-blue-200">gender</span><span className="text-gray-400">: </span><span className="text-amber-300">"male"</span><span className="text-gray-400">, </span><span className="text-blue-200">budget</span><span className="text-gray-400">: [</span><span className="text-green-300">30000</span><span className="text-gray-400">, </span><span className="text-green-300">50000</span><span className="text-gray-400">{'] },'}</span>{'\n'}
                  {'  '}<span className="text-blue-200">limit</span><span className="text-gray-400">: </span><span className="text-green-300">5</span>{'\n'}
                  <span className="text-gray-400">{'});'}</span>{'\n'}{'\n'}
                  <span className="text-gray-500">{'// results.items → Rangsorolt termékek AI indoklással'}</span>
                </code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeveloperSection;
